import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { GoogleLogo } from "@/components/GoogleLogo";
import { testimoniosData } from "@/components/TestimoniosCarousel";
import { Reveal } from "./Reveal";

// ---------------------------------------------------------------------------
// TESTIMONIALS — featured result + video testimonials + Google reviews carousel.
// Real quotes only (no invented metrics). Ordered by RESULT > CONTEXT > OPINIÓN.
// ---------------------------------------------------------------------------

const featured = {
  quote:
    "Teníamos 800 contactos durmiendo en un Excel. En 6 meses se reactivó la base y vendimos 12 departamentos.",
  name: "Facundo Stagnaro",
  business: "Hive Desarrollos · Desarrolladora inmobiliaria",
  initials: "FS",
};

const videos = testimoniosData
  .filter((t) => t.videoUrl)
  .map((t) => ({
    name: t.name,
    company: t.role,
    tag: t.metric,
    videoSrc: t.videoUrl!,
  }));

// Real Google-style reviews (text-only testimonials).
const reviews = testimoniosData
  .filter((t) => !t.videoUrl)
  .map((t) => ({
    initials: t.initials,
    quote: t.quote,
    name: t.name,
    company: t.role,
    tag: t.metric,
  }));

function VideoCard({
  v,
}: {
  v: { name: string; company: string; tag: string; videoSrc: string };
}) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      className="relative rounded-[20px] overflow-hidden border border-[#221F2A] bg-[#13111A]"
      style={{ aspectRatio: "9 / 16" }}
    >
      {playing ? (
        <video
          src={v.videoSrc}
          controls
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover bg-black"
        />
      ) : (
        <>
          <video
            src={`${v.videoSrc}#t=0.001`}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
            muted
            playsInline
            preload="metadata"
          />
          <div
            className="absolute inset-0 flex flex-col justify-between p-[18px] pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg,rgba(10,9,13,0) 55%,rgba(10,9,13,0.92) 100%)",
            }}
          >
            <div className="flex justify-end">
              <GoogleLogo className="w-[22px] h-[22px]" />
            </div>
            <div className="grid place-items-center">
              <button
                onClick={() => setPlaying(true)}
                aria-label={`Reproducir video de ${v.name}`}
                className="pointer-events-auto grid place-items-center rounded-full cursor-pointer transition-colors"
                style={{
                  width: 64,
                  height: 64,
                  border: "none",
                  background: "rgba(139,92,246,0.32)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M7 4.5v15l12.5-7.5z" fill="#8B5CF6" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-2.5">
              <div>
                <div className="font-semibold text-[#F4F2F7]">{v.name}</div>
                <div className="text-[14px] text-[#BAB6C4] mt-0.5">
                  {v.company}
                </div>
              </div>
              <span
                className="self-start font-mono font-medium rounded-[6px] py-1.5 px-2.5"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  color: "#34D399",
                  background: "rgba(10,9,13,0.6)",
                  border: "1px solid rgba(52,211,153,0.3)",
                }}
              >
                {v.tag}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function HomeTestimonials() {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 460, behavior: "smooth" });
  };

  return (
    <section
      data-screen-label="Testimonios"
      id="testimonios"
      className="border-t border-b border-[#221F2A] bg-[#0F0D14] scroll-mt-16"
    >
      <div
        className="mx-auto px-6"
        style={{
          maxWidth: 1240,
          paddingTop: "clamp(80px,10vw,128px)",
          paddingBottom: "clamp(80px,10vw,128px)",
        }}
      >
        <Reveal>
          <div
            className="font-mono text-[13px] text-[#A78BFA]"
            style={{ letterSpacing: "0.08em" }}
          >
            10 — RESEÑAS
          </div>
        </Reveal>

        {/* Featured */}
        <motion.figure
          className="mt-8 max-w-[1040px]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <blockquote
            className="text-[#EEEDF2]"
            style={{
              fontSize: "clamp(30px,3.8vw,52px)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textWrap: "pretty" as never,
            }}
          >
            “{featured.quote.split("12 departamentos")[0]}
            <span className="text-[#A78BFA]">12 departamentos</span>.”
          </blockquote>
          <figcaption className="flex items-center gap-3 mt-7">
            <div
              className="rounded-full grid place-items-center font-semibold text-[#A78BFA]"
              style={{
                width: 44,
                height: 44,
                background: "#251E3A",
                fontSize: 14,
              }}
            >
              {featured.initials}
            </div>
            <div>
              <div className="font-semibold text-[#EEEDF2]">
                {featured.name}
              </div>
              <div className="text-[14px] text-[#9C98A6]">
                {featured.business}
              </div>
            </div>
          </figcaption>
        </motion.figure>

        {/* En video */}
        <Reveal delay={0.1}>
          <div className="mt-[88px]">
            <h3
              className="font-medium text-[#EEEDF2]"
              style={{
                fontSize: "clamp(28px,3vw,40px)",
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
              }}
            >
              En video.
            </h3>
            <p className="text-[#9C98A6] mt-2.5 text-[16px]">
              Grabados por los mismos dueños.
            </p>
          </div>
        </Reveal>
        <div
          className="grid gap-5 mt-8"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill,minmax(min(100%,240px),1fr))",
          }}
        >
          {videos.map((v) => (
            <VideoCard key={v.name} v={v} />
          ))}
        </div>

        {/* Reseñas en Google */}
        <Reveal delay={0.1}>
          <div className="flex justify-between items-end gap-6 flex-wrap mt-20">
            <div>
              <h3
                className="font-medium text-[#EEEDF2]"
                style={{
                  fontSize: "clamp(28px,3vw,40px)",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                }}
              >
                Reseñas en Google.
              </h3>
              <p className="text-[#9C98A6] mt-2.5 text-[16px]">
                Escritas por clientes, con nombre y apellido.
              </p>
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={() => scrollBy(-1)}
                aria-label="Anterior"
                className="grid place-items-center rounded-full cursor-pointer transition-colors hover:border-[#8B5CF6]"
                style={{
                  width: 44,
                  height: 44,
                  border: "1px solid #2E2A38",
                  background: "#0A090D",
                  color: "#F4F2F7",
                  fontSize: 18,
                }}
              >
                ←
              </button>
              <button
                onClick={() => scrollBy(1)}
                aria-label="Siguiente"
                className="grid place-items-center rounded-full cursor-pointer transition-colors hover:border-[#8B5CF6]"
                style={{
                  width: 44,
                  height: 44,
                  border: "1px solid #2E2A38",
                  background: "#0A090D",
                  color: "#F4F2F7",
                  fontSize: 18,
                }}
              >
                →
              </button>
            </div>
          </div>
        </Reveal>
        <div
          ref={trackRef}
          className="flex gap-5 overflow-x-auto scrollbar-none mt-8 pb-1"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {reviews.map((r) => (
            <div
              key={r.name + r.quote.slice(0, 12)}
              className="border border-[#221F2A] rounded-[20px] bg-[#0A090D] flex flex-col gap-6"
              style={{
                flex: "0 0 min(86%,440px)",
                scrollSnapAlign: "start",
                padding: "clamp(24px,3vw,36px)",
              }}
            >
              <div className="flex justify-between items-center">
                <div
                  className="rounded-full grid place-items-center font-medium text-[#F4F2F7]"
                  style={{
                    width: 52,
                    height: 52,
                    border: "1px solid #2E2A38",
                    fontSize: 15,
                  }}
                >
                  {r.initials}
                </div>
                <div className="flex items-center gap-2.5">
                  <span
                    className="text-[#FBBC04]"
                    style={{ fontSize: 14, letterSpacing: "2px" }}
                  >
                    ★★★★★
                  </span>
                  <GoogleLogo className="w-[22px] h-[22px]" />
                </div>
              </div>
              <div className="relative pt-1">
                <span
                  className="absolute font-serif"
                  style={{
                    left: -14,
                    top: -14,
                    fontSize: 44,
                    lineHeight: 1,
                    color: "#4C3A7A",
                  }}
                >
                  “
                </span>
                <p
                  className="text-[#F4F2F7]"
                  style={{
                    fontSize: "clamp(17px,1.5vw,20px)",
                    lineHeight: 1.6,
                    textWrap: "pretty" as never,
                  }}
                >
                  {r.quote}
                </p>
              </div>
              <div className="mt-auto">
                <div className="font-semibold text-[#EEEDF2]">{r.name}</div>
                <div className="text-[14px] text-[#9C98A6] mt-1">
                  {r.company}
                </div>
              </div>
              <span
                className="self-start font-mono font-medium rounded-[6px] py-1.5 px-3"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  color: "#34D399",
                  background: "rgba(52,211,153,0.08)",
                  border: "1px solid rgba(52,211,153,0.3)",
                }}
              >
                {r.tag}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <a
            href="/testimonios"
            className="inline-flex items-center gap-2 text-[#A78BFA] font-mono text-[13px] tracking-[0.08em] uppercase hover:text-[#C4B5FD] transition-colors"
          >
            Ver todas las reseñas →
          </a>
        </div>
      </div>
    </section>
  );
}
